import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Tag, Button, message } from 'antd';
import OrderDetailModal from '../orders/OrderDetailModal';
import axios from 'axios';

const { Title } = Typography;

const ExceptionHandling = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axios.get('/api/users/customers/');
        const uniqueCustomers = response.data.reduce((acc, curr) => {
          if (!acc.find(c => c.id === curr.id)) {
            acc.push(curr);
          }
          return acc;
        }, []);
        setCustomers(uniqueCustomers);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  const fetchOrders = async (page = 1, customerId = selectedCustomer) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/orders/all/', {
        params: {
          page,
          is_deleted: false,
          is_modified: true,
          customer: customerId
        }
      });
      if (response.data && response.data.results) {
        setOrders(response.data.results);
        setPagination({
          current: page,
          pageSize: 20,
          total: response.data.count || 0,
        });
      } else {
        setOrders([]);
        setPagination({
          current: 1,
          pageSize: 20,
          total: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleTableChange = (pagination, filters) => {
    const customerId = filters.customerName?.[0];
    setSelectedCustomer(customerId);
    fetchOrders(pagination.current, customerId);
  };

  const handleMarkAsProcessed = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要处理的订单');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/orders/batch_update_modified/', {
        order_ids: selectedRowKeys
      });
      message.success('更新成功');
      setSelectedRowKeys([]);
      fetchOrders();
    } catch (error) {
      console.error('Error updating orders:', error);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case '重复订单':
        return { text: '重复订单', color: 'red' };
      case 'CREATED':
        return { text: '已创建', color: 'red' };
      case 'PRINTED':
        return { text: '已打印', color: 'red' };
      case 'SHIPPING':
        return { text: '运输中', color: 'blue' };
      case 'PENDING_REVIEW':
        return { text: '未审单', color: 'orange' };
      case 'PENDING_CUSTOMS':
        return { text: '待清关', color: 'warning' };
      case 'PICKED_UP':
        return { text: '已提货未过机', color: 'processing' };
      case 'INSPECTED_PENDING':
        return { text: '已出查验未放行', color: 'error' };
      case 'RELEASED':
        return { text: '已放行', color: 'success' };
      case 'DELIVERING':
        return { text: '派送中', color: 'processing' };
      case 'DELIVERED':
        return { text: '已送达', color: 'success' };
      case 'CANCELLED':
        return { text: '已作废', color: 'gray' };
      default:
        return { text: status, color: 'default' };
    }
  };

  const handleViewDetails = (record) => {
    setSelectedOrder(record);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedOrder(null);
  };

  const columns = [
    {
      title: '客户',
      dataIndex: 'customer_name',
      key: 'customerName',
      width: 120,
      filterMode: 'menu',
      filters: customers.map(customer => ({
        text: customer.company_name || customer.username,
        value: customer.id
      })),
      onFilter: (value, record) => {
        const customer = customers.find(c => c.id === value);
        return record.customer_name === (customer?.company_name || customer?.username);
      },
      filteredValue: selectedCustomer ? [selectedCustomer] : null,
      filterSearch: true,
    },
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'orderNumber',
      width: 150,
    },
    {
      title: '快递单号',
      dataIndex: 'express_number',
      key: 'expressNumber',
      width: 150,
    },
    {
      title: '空运单',
      dataIndex: 'airway_bill_number',
      key: 'airwayBillNumber',
      width: 120,
    },
    {
      title: '批次号',
      dataIndex: 'batch_number',
      key: 'batchNumber',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const { text, color } = getStatusDisplay(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'createdAt',
      width: 150,
    }
  ];

  const data = orders.map(order => ({
    ...order,
    key: order.id,
    express_number: order.express_number || '-',
    batch_number: order.batch_number || '-',
    airway_bill_number: order.airway_bill_number || '-',
    created_at: new Date(order.created_at).toLocaleString()
  }));

  return (
    <div>
      <Title level={2}>异常件处理</Title>
      <Card bodyStyle={{ padding: '12px' }}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={handleMarkAsProcessed}
            disabled={selectedRowKeys.length === 0}
            loading={loading}
          >
            已处理 ({selectedRowKeys.length})
          </Button>
        </div>
        <Table 
          columns={columns}
          dataSource={data} 
          rowKey="key"
          loading={loading}
          size="small"
          rowSelection={rowSelection}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
          scroll={{ y: 'calc(100vh - 350px)' }}
          onRow={(record) => ({
            onClick: () => handleViewDetails(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      <OrderDetailModal
        visible={modalVisible}
        order={selectedOrder}
        onClose={handleCloseModal}
        onRefresh={fetchOrders}
      />
    </div>
  );
};

export default ExceptionHandling;
